import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';

// Normalized Jira issue from /api/jira/issues
export interface JiraIssue {
  id: string;
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  issueType: string;
  issueTypeIcon?: string;
  projectKey: string;
  projectName: string;
}

export interface JiraTransition {
  id: string;
  name: string;
  toStatus: string;
}

export interface JiraStatusEntry {
  statusCategory: string;
  color: string;
}

interface UseJiraIssuesResult {
  issues: JiraIssue[];
  inProgress: JiraIssue[];
  open: JiraIssue[];
  loading: boolean;
  error: string | null;
  configured: boolean | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch the configured Jira account's assigned open issues.
 * Mirrors useMondayTasks. Returns empty list (no error) when Jira is not configured.
 */
export const useJiraIssues = (): UseJiraIssuesResult => {
  const [issues, setIssues] = useState<JiraIssue[]>([]);
  const [inProgress, setInProgress] = useState<JiraIssue[]>([]);
  const [open, setOpen] = useState<JiraIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/jira/issues`, { withCredentials: true });
      setIssues(response.data.issues || []);
      setInProgress(response.data.inProgress || []);
      setOpen(response.data.open || []);
      setConfigured(true);
    } catch (err: any) {
      if (err.response?.status === 503) {
        // Jira not configured — fail silently like the Monday hook does.
        setError(null);
        setIssues([]);
        setInProgress([]);
        setOpen([]);
        setConfigured(err.response?.data?.configured === false ? false : null);
      } else {
        setError(err.response?.data?.error || 'Failed to fetch Jira issues');
        setConfigured(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  return {
    issues,
    inProgress,
    open,
    loading,
    error,
    configured,
    refetch: fetchIssues
  };
};

/**
 * Hook to fetch full detail for a single Jira issue.
 */
export const useJiraIssueDetail = (issueKey: string | null) => {
  const [issue, setIssue] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async (key: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/jira/issues/${encodeURIComponent(key)}`, {
        withCredentials: true
      });
      setIssue(response.data);
    } catch (err: any) {
      if (err.response?.status === 503) {
        setError(null);
        setIssue(null);
      } else {
        setError(err.response?.data?.error || 'Failed to fetch Jira issue detail');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (issueKey) {
      fetchDetail(issueKey);
    } else {
      setIssue(null);
    }
  }, [issueKey, fetchDetail]);

  return { issue, loading, error, refetch: () => issueKey && fetchDetail(issueKey) };
};

/**
 * Hook to fetch workflow transitions for a single Jira issue on demand.
 */
export const useJiraTransitions = (issueKey: string | null) => {
  const [transitions, setTransitions] = useState<JiraTransition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTransitions = useCallback(async (key: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/jira/issues/${encodeURIComponent(key)}/transitions`,
        { withCredentials: true }
      );
      setTransitions(response.data.transitions || []);
    } catch (err: any) {
      if (err.response?.status === 503) {
        setError(null);
        setTransitions([]);
      } else {
        setError(err.response?.data?.error || 'Failed to fetch Jira transitions');
        setTransitions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (issueKey) {
      fetchTransitions(issueKey);
    } else {
      setTransitions([]);
      setError(null);
      setLoading(false);
    }
  }, [issueKey, fetchTransitions]);

  return {
    transitions,
    loading,
    error,
    refetch: () => issueKey && fetchTransitions(issueKey)
  };
};

/**
 * Hook to fetch all configured Jira statuses once on mount.
 * Returns a map keyed by lowercase status name → { statusCategory, color }.
 */
export const useJiraStatuses = () => {
  const [statusMap, setStatusMap] = useState<Record<string, JiraStatusEntry>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/jira/statuses`, { withCredentials: true });
      const list: Array<{ id: string; name: string; statusCategory: string; color: string }> =
        response.data.statuses || [];
      const map: Record<string, JiraStatusEntry> = {};
      for (const s of list) {
        map[String(s.name || '').toLowerCase().trim()] = {
          statusCategory: s.statusCategory,
          color: s.color
        };
      }
      setStatusMap(map);
    } catch (err: any) {
      if (err.response?.status === 503) {
        setError(null);
        setStatusMap({});
      } else {
        setError(err.response?.data?.error || 'Failed to fetch Jira statuses');
        setStatusMap({});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  return { statusMap, loading, error, refetch: fetchStatuses };
};

/**
 * Hook to resolve the Jira → eDoss bridge for a given issue key.
 * Returns the existing eDoss task (if any) or seed data for task creation.
 */
export const useJiraStartInEdoss = () => {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async (issueKey: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/jira/issues/${encodeURIComponent(issueKey)}/start-in-edoss`,
        { withCredentials: true }
      );
      setData(response.data);
      return response.data;
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to resolve Jira issue in eDoss';
      setError(message);
      setData(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, resolve };
};

/**
 * Hook for Jira workflow actions (transition / complete / start progress / worklog).
 */
export const useJiraActions = () => {
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const transition = useCallback(async (issueKey: string, status: string) => {
    setBusyKey(issueKey);
    try {
      const response = await axios.post(
        `${API_URL}/jira/issues/${encodeURIComponent(issueKey)}/transition`,
        { status },
        { withCredentials: true }
      );
      return response.data;
    } finally {
      setBusyKey(null);
    }
  }, []);

  const complete = useCallback(async (issueKey: string, status = 'Done') => {
    setBusyKey(issueKey);
    try {
      const response = await axios.post(
        `${API_URL}/jira/issues/${encodeURIComponent(issueKey)}/complete`,
        { status },
        { withCredentials: true }
      );
      return response.data;
    } finally {
      setBusyKey(null);
    }
  }, []);

  const startProgress = useCallback(async (issueKey: string) => {
    setBusyKey(issueKey);
    try {
      const response = await axios.post(
        `${API_URL}/jira/issues/${encodeURIComponent(issueKey)}/start-progress`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } finally {
      setBusyKey(null);
    }
  }, []);

  const addWorklog = useCallback(
    async (issueKey: string, seconds: number, metadata?: { taskName?: string; projectKey?: string }) => {
      setBusyKey(issueKey);
      try {
        const response = await axios.post(
          `${API_URL}/jira/issues/${encodeURIComponent(issueKey)}/worklog`,
          { seconds, ...metadata },
          { withCredentials: true }
        );
        return response.data;
      } finally {
        setBusyKey(null);
      }
    },
    []
  );

  return { busyKey, transition, complete, startProgress, addWorklog };
};

export default useJiraIssues;
